/**
 * Utility functions for SubKrewes
 */
import { memberShouldPayDues } from '~/composables/useShouldPayDues.js'
import type { SubKrewe } from '~/shared/types/subkrewe.js'

/**
 * Determines if a SubKrewe is active
 * A SubKrewe is considered active if at least one captain doesn't have to pay dues
 * Abandoned SubKrewes are never considered active
 *
 * @param subkrewe The SubKrewe to check
 * @returns Boolean indicating if the SubKrewe is active
 */
export function isSubKreweActive(subkrewe: SubKrewe | null | undefined): boolean {
  if (!subkrewe) return false

  // Abandoned SubKrewes are never active
  if (subkrewe.abandoned) return false

  // Check if the SubKrewe has captains
  const hasCaptains = subkrewe.captains && subkrewe.captains.length > 0

  // If there are no captains, it's not active
  if (!hasCaptains) return false

  // SubKrewe is active if at least one captain doesn't have to pay dues
  return subkrewe.captains.some(captain => !memberShouldPayDues(captain))
}

/**
 * Whether the member browser hides this SubKrewe only because no captain has
 * paid dues (or is exempt), so members can't find it to join. Abandoned and
 * denied SubKrewes are out for their own reasons; one without captains shows
 * as waiting for one.
 */
export function isHiddenForCaptainDues(subkrewe: SubKrewe | null | undefined): boolean {
  if (!subkrewe || subkrewe.abandoned || subkrewe.approved === 'No') return false
  if (!subkrewe.captains?.length) return false
  return !isSubKreweActive(subkrewe)
}

/**
 * Gets a badge severity for SubKrewe active status
 *
 * @param isActive Whether the SubKrewe is active
 * @returns Badge severity string for UI display
 */
export function getActiveStatusSeverity(isActive: boolean): string {
  return isActive ? 'success' : 'danger'
}

/**
 * Determines if a SubKrewe is pending registration
 * A SubKrewe is considered pending registration if it is approved but has no captains
 *
 * @param subkrewe The SubKrewe to check
 * @returns Boolean indicating if the SubKrewe is pending registration
 */
export function isSubkrewePendingRegistration(subkrewe: SubKrewe | null | undefined): boolean {
  if (!subkrewe) return false

  // Check if the SubKrewe is pending registration
  return subkrewe.approved === 'Yes' && subkrewe.captains && subkrewe.captains.length === 0
}

/**
 * Gets the SubKrewe status (active, inactive, or abandoned)
 *
 * @param subkrewe The SubKrewe to check
 * @returns Status string ('active', 'inactive', or 'abandoned')
 */
export function getSubKreweStatus(subkrewe: SubKrewe | null | undefined): string {
  if (!subkrewe) return 'inactive'

  if (subkrewe.abandoned) return 'abandoned'

  return isSubKreweActive(subkrewe) ? 'active' : 'inactive'
}

/**
 * Gets a badge severity for SubKrewe status
 *
 * @param status The SubKrewe status ('active', 'inactive', or 'abandoned')
 * @returns Badge severity string for UI display
 */
export function getStatusSeverity(status: string): string {
  switch (status.toLowerCase()) {
    case 'active': return 'success'
    case 'abandoned': return 'danger'
    case 'inactive':
    default: return 'warning'
  }
}
